import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Loader2 } from 'lucide-react';
import { foundation } from '../../../ui';
import { fg } from '../../../ui/theme/semanticTokens';
import AutoHeight from '../../common/AutoHeight';
import FadeStep from '../../common/FadeStep';
import useReducedMotion from '../../../hooks/ai/useReducedMotion';
import { useResponsiveLayout } from '../../../hooks/ui/useResponsiveLayout';
import { useModalKeyboard } from '../../../hooks/ui/useModalKeyboard';
import useIsLandscapePhone from '../../../hooks/ui/useIsLandscapePhone';
import { useAuth } from '../../../AuthContext';
import { mfaTotpIssuer } from '../../../utils/auth/mfaTotpIssuer';
import { modalBodyScrollRegion } from '../../../ui/overlayStyles';
import { mfaHeaderWrap, mfaFooterRowStyle, mfaFooterRowFor, mfaPrimaryCta, mfaSecondaryCta } from './mfaStyles';
import { ensureSpinStyle, SPIN_ANIMATION } from '../../../utils/ui/spinAnimation';
import useMfaEnrollment from './useMfaEnrollment';
import { peekMfaResetNote, prefetchMfaResetNote, MFA_RESET_NOTE_PREFETCH_CAP_MS } from './mfaResetNoteCache';
import MfaStepperHeader from './MfaStepperHeader';
import MfaStepIntro from './MfaStepIntro';
import MfaStepAdd from './MfaStepAdd';
import MfaStepCode from './MfaStepCode';
import MfaStepResume from './MfaStepResume';
import MfaStepRecoveryCodes from './MfaStepRecoveryCodes';
import MfaStepDone from './MfaStepDone';

const f = foundation;

const STEP_SEQUENCE = ['intro', 'add', 'code', 'codes', 'done'];
const RESUME_SEQUENCE = ['resume', 'codes', 'done'];
const TOTAL_STEPS = 4;


/** { current, total } for the header's bars/label, or null on the un-numbered
 *  intro/checking steps. Resume skips "step 1" (adding the app already
 *  happened before the reload) — it starts numbered at 2. */
function stepNumbering(step, resumeFlow) {
  if (step === 'intro' || step === 'checking') return null;
  const seq = resumeFlow ? RESUME_SEQUENCE : STEP_SEQUENCE;
  const i = seq.indexOf(step);
  if (i < 0) return null;
  return { current: resumeFlow ? i + 2 : i, total: TOTAL_STEPS };
}

/**
 * RD-SEC-015 — the MFA enrollment stepper's state + presentation, split into
 * three independent nodes so callers can place each in a TRUE separate flex
 * region: `header` and `footer` are `flexShrink:0`, OUTSIDE the scroll
 * region; `body` is the ONE scroller (modal max-height standing order,
 * AGENTS.md). Before 2026-09-25 the header was `position: sticky` INSIDE the
 * scrollable body and every step's CTA button was rendered inline inside its
 * own step component (also inside that same scrollable body) — this hook is
 * the fix: no step component owns its own CTA anymore, `footer` here does,
 * computed from the SAME `useMfaEnrollment` state the body already reads.
 *
 * `mode: 'account'` — used by `MfaSetupModal` (via the `MfaStepper` wrapper
 * below). `mode: 'gate'` — used by both forced enrollment gates, which call
 * this hook directly since `LoginAccessCard` already owns its own
 * header/body/footer regions; the caller passes `onEnrollmentComplete` /
 * `onRecoveryHoldChange` straight through to `useMfaEnrollment` (LOCKED
 * hold/complete contract, unchanged from the pre-RD-SEC-015 enroll flow) and
 * `onFooterHiddenChange` to hide its own sign-out footer on codes/done.
 *
 * RD-SEC-015 (2026-09-25 "modal grows after opening" fix): the reset note
 * (`mfaResetNoteCache.js`) must be KNOWN before the intro step is revealed,
 * or it arrives a beat late and grows the modal a second time right as the
 * user starts reading it. `TwoFactorSettingsRow` prefetches it on mount, so
 * `peekMfaResetNote` usually already has the answer by the time this hook
 * runs; when it doesn't (both forced gates have no earlier row-mount to
 * prefetch from), this hook prefetches it itself and holds the 'intro' step
 * on the SAME minimal placeholder the 'checking' step already uses, capped
 * at `MFA_RESET_NOTE_PREFETCH_CAP_MS` — past the cap, intro reveals without
 * the note and a late arrival eases in through the body's single AutoHeight
 * (no snap).
 *
 * `sideways` (KAN-1012, the account pop-up on a phone held sideways, passed by `MfaStepper` only):
 * tighter header gap, title / subtitle gaps and 44px code boxes. The forced gates never pass it.
 */
export function useMfaStepperUi({
  mode = 'account', onClose, onEnrollmentComplete, onRecoveryHoldChange, onFooterHiddenChange, sideways = false,
}) {
  const intl = useIntl();
  const reducedMotion = useReducedMotion();
  const { isMobile, isTablet } = useResponsiveLayout();
  const compact = isMobile || isTablet;
  const { sessionUser, tenantId } = useAuth();
  const userId = sessionUser?.id || null;
  useEffect(() => {
    ensureSpinStyle();
  }, []);
  const [resetNote, setResetNote] = useState(() => {
    const cached = peekMfaResetNote(userId);
    return cached === undefined ? null : cached;
  });
  const [noteReady, setNoteReady] = useState(() => peekMfaResetNote(userId) !== undefined);
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    if (!userId) {
      setNoteReady(true);
      return undefined;
    }
    const cached = peekMfaResetNote(userId);
    if (cached !== undefined) {
      setResetNote(cached);
      setNoteReady(true);
      return undefined;
    }
    let active = true;
    let capTimer = setTimeout(() => {
      capTimer = null;
      if (active) setNoteReady(true);
    }, MFA_RESET_NOTE_PREFETCH_CAP_MS);
    prefetchMfaResetNote({ userId, tenantId }).then((variant) => {
      if (capTimer != null) {
        clearTimeout(capTimer);
        capTimer = null;
      }
      if (!active) return;
      setResetNote(variant);
      setNoteReady(true);
    });
    return () => {
      active = false;
      if (capTimer != null) clearTimeout(capTimer);
    };
  }, [userId, tenantId]);

  const enrollment = useMfaEnrollment({ mode, onEnrollmentComplete, onRecoveryHoldChange });
  const {
    step, payload, code, setCode, codeStatus, missCount, codes, codesFailed, busy, startError, fromApp, resumeFlow,
    startEnroll, goToCode, goBackToAdd, goBackToIntro, verify, startOver, cancelPending, doneAction, markLeftForApp,
    setStep,
  } = enrollment;

  useEffect(() => {
    onFooterHiddenChange?.(step === 'codes' || step === 'done');
  }, [step, onFooterHiddenChange]);

  useEffect(() => {
    if (step === 'codes') setAcknowledged(false);
  }, [step]);

  const canBack = step === 'add' || step === 'code';
  const canClose = mode === 'account' && ['intro', 'add', 'code'].includes(step);
  const numbering = stepNumbering(step, resumeFlow);
  const headerLabel = numbering
    ? intl.formatMessage({ id: 'mfaStepper.stepOf', defaultMessage: 'Step {a} of {b}' }, { a: numbering.current, b: numbering.total })
    : intl.formatMessage({ id: 'mfaStepper.pre', defaultMessage: 'Before you start' });

  const handleBack = useCallback(() => {
    // Defensive early return matching the header's own hidden/unreachable
    // gating (P2d) — a caller that invokes this outside a real button click
    // (e.g. a keyboard handler) must see the SAME "hidden control does
    // nothing" contract, not just a visually-unreachable one.
    if (!canBack) return;
    if (step === 'code') goBackToAdd();
    else if (step === 'add') goBackToIntro();
  }, [canBack, step, goBackToAdd, goBackToIntro]);

  const handleClose = useCallback(() => {
    // Same defensive gate as handleBack — critically, this keeps gate mode's
    // "no reachable ✕ on any step" contract even if something calls this
    // directly (canClose is `mode === 'account' && ...`, so it's always
    // false for the forced gates): cancelPending must never fire from a
    // hidden/unreachable close control.
    if (!canClose) return;
    void cancelPending().then(() => onClose?.());
  }, [canClose, cancelPending, onClose]);

  const handleAppHandoffReturn = useCallback(() => {
    markLeftForApp();
    goToCode();
  }, [markLeftForApp, goToCode]);

  const issuer = mfaTotpIssuer();
  const codesIssued = codes || [];
  const codeReady = code.length === 6;
  const primaryButtonStyle = (disabled) => mfaPrimaryCta(disabled, compact);
  const secondaryButtonStyle = (disabled) => ({ ...mfaSecondaryCta(disabled, compact), marginTop: f.spacePx[2] });

  // Real 'checking' step (mount-time resume probe) OR an already-resolved
  // 'intro' step still waiting on the reset-note prefetch to settle (capped
  // above) — one shared "not ready to reveal yet" frame, same height class,
  // so the modal never shows an intro that's about to grow a beat later.
  const introPendingNote = step === 'intro' && !noteReady;
  let content = null;
  let footer = null;
  if (step === 'checking' || introPendingNote) {
    content = (
      <div
        data-testid="mfa-checking"
        role="status"
        aria-busy="true"
        aria-atomic="true"
        style={{
          minHeight: 160,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          color: fg.muted,
          fontSize: 14,
        }}
      >
        <Loader2
          size={16}
          strokeWidth={2.2}
          aria-hidden="true"
          style={{ animation: reducedMotion ? 'none' : SPIN_ANIMATION, flexShrink: 0 }}
        />
        <span>{intl.formatMessage({ id: 'common.checking', defaultMessage: 'Checking…' })}</span>
      </div>
    );
  } else if (step === 'intro') {
    content = <MfaStepIntro gate={mode === 'gate'} resetNote={resetNote} errorMessage={startError} />;
    footer = (
      <button type="button" style={primaryButtonStyle(busy)} disabled={busy} onClick={startEnroll}>
        {intl.formatMessage({ id: 'mfaStepper.start', defaultMessage: 'Get started' })}
      </button>
    );
  } else if (step === 'add') {
    content = <MfaStepAdd compact={compact} payload={payload} onHandoffReturn={handleAppHandoffReturn} />;
    footer = (
      <button type="button" style={primaryButtonStyle(busy)} disabled={busy} onClick={goToCode}>
        {intl.formatMessage({ id: 'mfaStepper.add.done', defaultMessage: 'I added it' })}
      </button>
    );
  } else if (step === 'code') {
    content = (
      <MfaStepCode
        code={code}
        onChange={setCode}
        status={codeStatus}
        missCount={missCount}
        busy={busy}
        fromApp={fromApp}
        onVerify={verify}
        onRescan={goBackToAdd}
        sideways={sideways}
      />
    );
    footer = (
      <button type="button" style={primaryButtonStyle(busy || !codeReady)} disabled={busy || !codeReady} onClick={() => verify(code)}>
        {intl.formatMessage({ id: 'mfaStepper.code.verify', defaultMessage: 'Verify' })}
      </button>
    );
  } else if (step === 'resume') {
    content = <MfaStepResume code={code} onChange={setCode} status={codeStatus} busy={busy} onVerify={verify} sideways={sideways} />;
    footer = (
      <div>
        <button type="button" style={primaryButtonStyle(busy || !codeReady)} disabled={busy || !codeReady} onClick={() => verify(code)}>
          {intl.formatMessage({ id: 'mfaStepper.code.verify', defaultMessage: 'Verify' })}
        </button>
        <button type="button" style={secondaryButtonStyle(busy)} disabled={busy} onClick={startOver}>
          {intl.formatMessage({ id: 'mfaStepper.resume.startOver', defaultMessage: 'Start over' })}
        </button>
      </div>
    );
  } else if (step === 'codes') {
    content = (
      <MfaStepRecoveryCodes
        codes={codesIssued}
        compact={compact}
        acknowledged={acknowledged}
        onAcknowledgedChange={setAcknowledged}
        userEmail={sessionUser?.email || ''}
      />
    );
    footer = (
      <button type="button" style={primaryButtonStyle(!acknowledged)} disabled={!acknowledged} onClick={() => setStep('done')}>
        {intl.formatMessage({ id: 'mfaStepper.continue', defaultMessage: 'Continue' })}
      </button>
    );
  } else if (step === 'done') {
    content = <MfaStepDone issuer={issuer} codesCount={codesIssued.length} codesFailed={codesFailed} />;
    footer = (
      <button type="button" style={primaryButtonStyle(false)} onClick={doneAction}>
        {mode === 'gate'
          ? intl.formatMessage({ id: 'mfaStepper.done.goHome', defaultMessage: 'Go to home' })
          : intl.formatMessage({ id: 'mfaStepper.done.done', defaultMessage: 'Done' })}
      </button>
    );
  }

  const header = (
    <div style={mfaHeaderWrap(sideways)}>
      <MfaStepperHeader
        variant="steps"
        compact={sideways}
        label={headerLabel}
        current={numbering?.current ?? 0}
        total={TOTAL_STEPS}
        canBack={canBack}
        canClose={canClose}
        onBack={handleBack}
        onClose={handleClose}
      />
    </div>
  );

  const body = (
    <AutoHeight>
      <FadeStep key={step} reduceMotion={reducedMotion}>
        {content}
      </FadeStep>
    </AutoHeight>
  );

  return { header, body, footer, canClose, requestClose: handleClose };
}

/**
 * Self-contained wrapper around `useMfaStepperUi` for callers that own a
 * single `modalFlexColumnShell` dialog (`MfaSetupModal`) and can render the
 * three pieces as direct flex-column children — header, a scroll-wrapped
 * body (`modalBodyScrollRegion`), and a pinned footer
 * (`mfaFooterRowStyle`), mirroring `MfaTurnOffModal` /
 * `MfaRegenerateCodesModal`'s hand-written anatomy. The gates don't use this
 * wrapper — they call `useMfaStepperUi` directly because `LoginAccessCard`
 * already owns its own header/body/footer regions (see
 * `TenantMfaEnrollmentGate`).
 */
export default function MfaStepper(props) {
  const sideways = useIsLandscapePhone();
  const { header, body, footer, requestClose } = useMfaStepperUi({ ...props, sideways });
  // Escape mirrors the ✕: `requestClose` (handleClose, above) already
  // no-ops when `!canClose` — the same gate the header's own ✕ button
  // renders unreachable under (P2d) — so this is safe to wire unconditionally
  // for every step, including gate-mode-equivalent steps where the account
  // modal itself withholds the ✕ (e.g. the 'codes'/'done' steps' "must
  // acknowledge to leave" rule).
  useModalKeyboard(true, { onEscape: requestClose });
  return (
    <>
      {header}
      <div style={modalBodyScrollRegion}>{body}</div>
      {footer ? <div style={mfaFooterRowFor(mfaFooterRowStyle, sideways)}>{footer}</div> : null}
    </>
  );
}
